"use client";

import type { CricketLiveEvent, CricketLiveEventMatchup } from "@/api/home";
import cricketImage from "@/assets/global/cricket.webp";
import { noBtnCls, yesBtnCls } from "@/components/platform/style";
import { useTradePanel } from "@/components/trade-panel/hooks/use-trade-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { FavoriteStar } from "@/components/ui/favorite-star";
import { LiveIcon } from "@/components/ui/live-icon";
import { SmartImage } from "@/components/ui/smart-image";
import { useEventFollow } from "@/hooks/use-event-follow";
import { cn } from "@/lib/utils";
import { formatMonetaryValue, formatUnitPrice, getOptionLabel } from "@/utils/utils";
import { BigMath } from "@yn/utils";
import Autoplay from "embla-carousel-autoplay";
import { usePathname } from "next/navigation";
import { type MouseEvent, useCallback, useEffect, useRef, useState } from "react";

const TEAM_IMAGE_OPTIONS = { width: 150, height: 150 };
const TEAM_LOGO_IMAGE_OPTIONS = { width: 100, height: 100 };

type MatchupView = ReturnType<typeof resolveMatchup>;

function resolveMatchup( matchups: CricketLiveEventMatchup[] | undefined, index: number ) {
    const matchup = matchups?.[index];
    return {
        name: matchup?.title || matchup?.abbr || "",
        abbr: matchup?.abbr || matchup?.title || "",
        matchupImageUrl: matchup?.matchupImageUrl,
        matchRecord: matchup?.matchRecord,
    };
}

function splitScoreRecord( record?: string ) {
    const firstInning = record?.split( "&" )[0]?.trim() ?? "";
    const scoreMatch = firstInning.match( /^(\d+)\/(\d+)\s*\(([\d.]*)\s*ov\)/i );
    const over = scoreMatch?.[3] ? scoreMatch[3] : "0";
    const normalizedOver = over.includes( "." ) ? over : `${over}.0`;
    const normalized = scoreMatch ? `${scoreMatch[1]}-${scoreMatch[2]} (${normalizedOver})` : firstInning;
    const match = normalized.match( /^(.+?)\s*(\([^)]+\))$/ );
    if ( !match ) {
        return { scoreMain: normalized || "0-0", overs: "" };
    }
    return { scoreMain: match[1].trim(), overs: match[2] };
}

function formatOverDisplay( display?: string ) {
    if ( !display ) return "";
    const trimmed = display.trim();
    return trimmed.startsWith( "(" ) ? trimmed : `(${trimmed})`;
}

function isTeamNameMatch( source: string | undefined, candidates: string[] ) {
    if ( !source ) return false;
    const normalizedSource = source.toLowerCase();
    return candidates.some( candidate => candidate.toLowerCase() === normalizedSource );
}

function formatPercent( value?: number ) {
    const percent = value;
    if ( BigMath.lt( percent, 0 ) ) return 0;
    if ( BigMath.gt( percent, 100 ) ) return 100;
    return Number( percent );
}

function TeamLogo(
    { name, matchupImageUrl, size }: { name: string; matchupImageUrl?: string; size: "large" | "small" },
) {
    const isLarge = size === "large";
    const wrapperClassName = isLarge ? "size-12" : "size-8";
    const textClassName = isLarge ? "text-sm font-bold" : "text-xs font-semibold";

    if ( matchupImageUrl ) {
        return (
            <div className={cn( wrapperClassName, "shrink-0 overflow-hidden rounded bg-surface-muted" )}>
                <SmartImage
                    src={matchupImageUrl}
                    alt={name}
                    width={isLarge ? 48 : 32}
                    height={isLarge ? 48 : 32}
                    className="size-full object-cover"
                    imageUrlOptions={isLarge ? TEAM_IMAGE_OPTIONS : TEAM_LOGO_IMAGE_OPTIONS}
                />
            </div>
        );
    }

    return (
        <div
            className={cn(
                "flex shrink-0 items-center justify-center rounded-lg bg-surface-input text-t2",
                wrapperClassName,
                textClassName,
            )}
        >
            {name}
        </div>
    );
}

function TeamColumn(
    { matchup, scoreMain, overs, isBatting }: {
        matchup: MatchupView;
        scoreMain: string;
        overs: string;
        isBatting: boolean;
    },
) {
    return (
        <div className="flex h-[134px] w-[97px] shrink-0 flex-col items-center gap-2">
            <TeamLogo name={matchup.name} matchupImageUrl={matchup.matchupImageUrl} size="large" />
            <p className="max-h-[34px] line-clamp-2 text-center text-sm font-bold leading-[17px] text-t1">
                {matchup.name}
            </p>
            <div className="flex flex-col items-center gap-1">
                <div className="flex h-5 items-center justify-center gap-1">
                    <span className={cn( "text-base font-semibold leading-5", isBatting ? "text-yes" : "text-t3" )}>
                        {scoreMain}
                    </span>
                    {isBatting && (
                        <SmartImage
                            src={cricketImage}
                            alt=""
                            aria-hidden
                            width={16}
                            height={16}
                            className="size-4 shrink-0"
                        />
                    )}
                </div>
                <span className={`text-xs font-normal leading-[15px] ${isBatting ? "text-t2" : "text-t3"}`}>
                    {overs || "(0.0)"}
                </span>
            </div>
        </div>
    );
}

function SpotlightBottom( { event, pagination }: { event: CricketLiveEvent; pagination: boolean } ) {
    const { followed: watchlisted, handleLoveClick } = useEventFollow( {
        eventNo: event.eventNo,
        followed: event.followed ?? false,
    } );

    return (
        <div
            className={cn(
                "flex w-full items-center justify-between border-t border-stroke px-4 py-2",
                pagination ? "" : "mt-3",
            )}
        >
            <div className="flex items-center gap-1">
                {event.liveState === 1 && (
                    <>
                        <LiveIcon />
                        <span className="text-xs font-bold uppercase text-state-danger">Live</span>
                        <span className="text-t3">·</span>
                    </>
                )}
                <span className="text-xs font-medium text-t3">{formatMonetaryValue( event.tradeAmount ?? 0 )} Vol</span>
            </div>
            <FavoriteStar active={watchlisted} className="size-7" onClick={handleLoveClick} />
        </div>
    );
}

function SpotlightCard( { event, onOpen }: { event: CricketLiveEvent; onOpen: ( event: CricketLiveEvent ) => void } ) {
    const { open } = useTradePanel();
    const pathname = usePathname();
    const guessList = Array.isArray( event.guessList ) ? event.guessList : [];
    const guess = guessList.find( item => Number( item.mainFlag ) === 1 ) ?? guessList[0];
    const livePanel = event.cricketLivePanel;
    const left = resolveMatchup( event.matchups, 0 );
    const right = resolveMatchup( event.matchups, 1 );
    const battingTeamName = livePanel?.battingTeam?.shortName;
    const leftScore = splitScoreRecord( left.matchRecord );
    const rightScore = splitScoreRecord( right.matchRecord );
    const liveScore = livePanel?.score?.display;
    const liveOver = livePanel?.over?.display;
    const leftIsBatting = isTeamNameMatch( battingTeamName, [ left.abbr, left.name ] );
    const rightIsBatting = isTeamNameMatch( battingTeamName, [ right.abbr, right.name ] );
    const yesPercent = guess ? formatPercent( guess.yesRealtimePrice ) : 50;
    const noPercent = guess ? formatPercent( guess.noRealtimePrice ) : 50;
    const leftScoreMain = leftIsBatting && liveScore ? liveScore : leftScore.scoreMain;
    const leftOvers = leftIsBatting && liveOver ? formatOverDisplay( liveOver ) : leftScore.overs;
    const rightScoreMain = rightIsBatting && liveScore ? liveScore : rightScore.scoreMain;
    const rightOvers = rightIsBatting && liveOver ? formatOverDisplay( liveOver ) : rightScore.overs;
    const handlePress = () => {
        if ( event.eventNo ) {
            onOpen( event );
        }
    };

    const handleButtonClick = ( e: MouseEvent, selectedOption: "yes" | "no" ) => {
        e.stopPropagation();
        if ( !guess ) return;

        open( { guessInfo: guess, eventInfo: event, detailInfo: event, selectedOption, tradeType: "buy", pathname } );
    };

    return (
        <Card
            className={cn(
                "border-none shadow-none rounded-none",
                event.eventNo && "cursor-pointer transition-opacity active:opacity-90",
            )}
            shadow={false}
        >
            <CardContent className="flex flex-col gap-0 p-0">
                <div
                    className="flex h-[206px] flex-col gap-2 px-3 py-3"
                    onClick={handlePress}
                    onKeyDown={( e ) => {
                        if ( event.eventNo && ( e.key === "Enter" || e.key === " " ) ) {
                            e.preventDefault();
                            handlePress();
                        }
                    }}
                    role={event.eventNo ? "button" : undefined}
                    tabIndex={event.eventNo ? 0 : undefined}
                >
                    <div className="flex h-[134px] w-full items-start justify-between gap-3">
                        <TeamColumn
                            matchup={left}
                            scoreMain={leftScoreMain}
                            overs={leftOvers}
                            isBatting={leftIsBatting}
                        />
                        <div className="flex w-[101px] shrink-0 flex-col items-center gap-2 pt-4">
                            <div className="flex items-center gap-2 whitespace-nowrap font-bold">
                                <span className="text-[18px] leading-[22px] text-yes">{yesPercent}%</span>
                                <span className="text-2xl font-semibold leading-[30px] text-t3">-</span>
                                <span className="text-[18px] leading-[22px] text-no">{noPercent}%</span>
                            </div>
                            <div className="flex h-2.5 w-[52px] shrink-0 items-center gap-0.5" aria-hidden>
                                <div className="h-full min-w-px bg-yes" style={{ flex: `${yesPercent} 1 0%` }} />
                                <div className="h-full min-w-px bg-no" style={{ flex: `${noPercent} 1 0%` }} />
                            </div>
                        </div>
                        <TeamColumn
                            matchup={right}
                            scoreMain={rightScoreMain}
                            overs={rightOvers}
                            isBatting={rightIsBatting}
                        />
                    </div>

                    <div className="flex min-w-0 flex-col">
                        <div className="flex w-full gap-2">
                            {( [ "yes", "no" ] as const ).map( option => {
                                const price = option === "yes" ? guess?.yesRealtimePrice : guess?.noRealtimePrice;
                                return (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={( e ) => handleButtonClick( e, option )}
                                        className={cn(
                                            "relative flex-1 h-10 rounded text-sm font-medium active:scale-95 transition-transform flex items-center gap-1 justify-center",
                                            option === "yes" ? yesBtnCls : noBtnCls,
                                        )}
                                    >
                                        <div className="text-sm truncate max-w-[85px]">
                                            {getOptionLabel( option, guess, event )}
                                        </div>
                                        <div>{formatUnitPrice( price ?? 0, { precision: 1 } )}</div>
                                    </button>
                                );
                            } )}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

interface SpotlightCarouselProps {
    events: CricketLiveEvent[];
    onEventNavigate?: ( event: CricketLiveEvent ) => void;
}

export function SpotlightCarousel( { events, onEventNavigate }: SpotlightCarouselProps ) {
    const autoplay = useRef( Autoplay( { delay: 5000, stopOnInteraction: false } ) );
    const [ api, setApi ] = useState<CarouselApi>();
    const [ current, setCurrent ] = useState( 0 );

    const onOpen = useCallback( ( event: CricketLiveEvent ) => {
        onEventNavigate?.( event );
    }, [ onEventNavigate ] );

    useEffect( () => {
        if ( !api ) return;
        const sync = () => setCurrent( api.selectedScrollSnap() );
        sync();
        api.on( "select", sync );
        api.on( "reInit", sync );
        return () => {
            api.off( "select", sync );
            api.off( "reInit", sync );
        };
    }, [ api ] );

    if ( events.length === 0 ) {
        return null;
    }

    const currentEvent = events[current] ?? events[0];

    return (
        <div className="mt-4 w-full overflow-hidden rounded-xl border border-stroke bg-surface-card relative">
            <Carousel
                opts={{ align: "start", loop: events.length > 1 }}
                plugins={events.length > 1 ? [ autoplay.current ] : undefined}
                setApi={setApi}
                className="w-full relative"
            >
                <CarouselContent className="-ml-0">
                    {events.map( event => (
                        <CarouselItem key={event.id || event.eventNo} className="pl-0">
                            <SpotlightCard event={event} onOpen={onOpen} />
                        </CarouselItem>
                    ) )}
                </CarouselContent>
                {events.length > 1 && (
                    <div
                        className="w-full mb-2 flex h-1.5 items-center justify-center gap-1"
                        onClick={( e ) => e.stopPropagation()}
                        onKeyDown={( e ) => e.stopPropagation()}
                    >
                        {Array.from(
                            { length: events.length },
                            ( _, index ) => (
                                <button
                                    key={index}
                                    type="button"
                                    aria-label={`Slide ${index + 1}`}
                                    aria-current={index === current ? true : undefined}
                                    className={cn(
                                        "rounded-full transition-[width,background-color]",
                                        index === current ? "h-1.5 w-5 bg-t1" : "size-1.5 bg-surface-emphasis",
                                    )}
                                    onClick={( e ) => {
                                        e.stopPropagation();
                                        api?.scrollTo( index );
                                    }}
                                >
                                </button>
                            ),
                        )}
                    </div>
                )}
            </Carousel>
            <SpotlightBottom key={currentEvent.eventNo} event={currentEvent} pagination={events.length > 1} />
        </div>
    );
}
